/* Mileage hooks. useMileageRate reads the AUTHORITATIVE IRS rate for a date from the
   accounting-api (GET /v1/expense/mileage-rate) — never a hardcoded client constant,
   so the displayed reimbursement matches what the server computes at posting.
   useAddMileageLine ensures the trip's monthly report exists (idempotent POST /reports)
   then appends a MILEAGE line; the server recomputes miles × rate authoritatively. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { expenseFetch } from '@/expense/lib/expense-api';
import type { ExpenseLine, ExpenseReport } from './use-reports';

export interface MileageRate {
  effectiveFrom: string;
  ratePerMile: string;
}

/** The IRS rate in effect on `effective` (YYYY-MM-DD). */
export function useMileageRate(effective: string) {
  return useQuery({
    queryKey: ['expense', 'mileage-rate', effective],
    queryFn: () =>
      expenseFetch<MileageRate>(
        `/v1/expense/mileage-rate?effective=${effective}`,
      ),
    // The rate table changes ~once a year — cache aggressively.
    staleTime: 60 * 60 * 1000,
  });
}

/** Add a mileage trip: ensure the month's report exists, then append the MILEAGE line. */
export function useAddMileageLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (trip: {
      txnDate: string;
      miles: string;
      description?: string;
      glAccountId?: number | null;
    }) => {
      const period = trip.txnDate.slice(0, 7);
      const report = await expenseFetch<ExpenseReport>('/v1/expense/reports', {
        method: 'POST',
        body: JSON.stringify({ period }),
      });
      return expenseFetch<ExpenseLine>(
        `/v1/expense/reports/${report.reportId}/lines`,
        {
          method: 'POST',
          body: JSON.stringify({
            lineType: 'MILEAGE',
            currency: 'USD',
            txnDate: trip.txnDate,
            description: trip.description,
            glAccountId: trip.glAccountId,
            mileageMiles: trip.miles,
            mileageRateEffective: trip.txnDate,
          }),
        },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expense', 'reports'] }),
  });
}
